import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Auth + layout
import { AuthCallback, AuthCallbackPage, ProtectedRoute } from './components/auth/AuthCallback';
import ClientLoginPage from './components/auth/ClientLoginPage';
import ClientPortal from './components/auth/ClientPortal';
import DashboardLayout from './components/layout/DashboardLayout';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardOverview from './pages/DashboardOverview';
import PentestPage from './pages/PentestPage';
import AccessibilityPage from './pages/AccessibilityPage';
import ControlsPage from './pages/ControlsPage';
import PoliciesPage from './pages/PoliciesPage';
import FindingsPage from './pages/FindingsPage';
import MonitorsPage from './pages/MonitorsPage';
import CAIQPage from './pages/CAIQPage';
import EvidencePage from './pages/EvidencePage';
import ReportsPage from './pages/ReportsPage';
import AuditTrailPage from './pages/AuditTrailPage';
import TrustCenterPreview from './pages/TrustCenterPreview';
import TrustCenterPage from './pages/TrustCenterPage';
import SecurityOverviewPage from './pages/SecurityOverviewPage';
import ClientsPage from './pages/ClientsPage';
import AuditPage from './pages/AuditPage';
import RosterPage from './pages/RosterPage';
import UserManagementPage from './pages/UserManagementPage';
import TrustRequestsPage from './pages/TrustRequestsPage';
import AccessControlPage from './pages/AccessControlPage';
import RequestAccessPage from './pages/RequestAccessPage';
import AccessRequestsPage from './pages/AccessRequestsPage';

/**
 * Helper that wraps a page in the standard <ProtectedRoute/><DashboardLayout/> shell.
 * Drastically cuts boilerplate vs. one inline JSX block per route.
 */
const Protected = (Component) => (
  <ProtectedRoute>
    <DashboardLayout>
      <Component />
    </DashboardLayout>
  </ProtectedRoute>
);

/**
 * Centralised route table. Edit a single line to add or remove a page.
 */
export default function AppRouter() {
  const location = useLocation();

  // Legacy Emergent SSO returns with `#session_id=...`. Route those to
  // the legacy callback handler unconditionally — they don't match any
  // route otherwise.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public */}
      <Route path="/login"             element={<LoginPage />} />
      <Route path="/auth/callback"     element={<AuthCallbackPage />} />
      <Route path="/client-login"      element={<ClientLoginPage />} />
      <Route path="/client-portal"     element={<ClientPortal />} />
      <Route path="/trust-center"      element={<TrustCenterPage />} />
      <Route path="/request-access"    element={<RequestAccessPage />} />
      <Route path="/security-overview" element={<SecurityOverviewPage />} />

      {/* Authenticated dashboard */}
      <Route path="/dashboard"             element={Protected(DashboardOverview)} />
      <Route path="/controls"              element={Protected(ControlsPage)} />
      <Route path="/policies"              element={Protected(PoliciesPage)} />
      <Route path="/roster"                element={Protected(RosterPage)} />
      <Route path="/users"                 element={Protected(UserManagementPage)} />
      <Route path="/trust-requests"        element={Protected(TrustRequestsPage)} />
      <Route path="/findings"              element={Protected(FindingsPage)} />
      <Route path="/monitors"              element={Protected(MonitorsPage)} />
      <Route path="/pentest"               element={Protected(PentestPage)} />
      <Route path="/accessibility"         element={Protected(AccessibilityPage)} />
      <Route path="/evidence"              element={Protected(EvidencePage)} />
      <Route path="/caiq"                  element={Protected(CAIQPage)} />
      <Route path="/reports"               element={Protected(ReportsPage)} />
      <Route path="/trust-center-preview"  element={Protected(TrustCenterPreview)} />
      <Route path="/clients"               element={Protected(ClientsPage)} />
      <Route path="/access-control"        element={Protected(AccessControlPage)} />
      <Route path="/access-requests"       element={Protected(AccessRequestsPage)} />
      <Route path="/audit"                 element={Protected(AuditPage)} />
      <Route path="/audit-trail"           element={Protected(AuditTrailPage)} />

      {/* Fallbacks */}
      <Route path="/" element={<Navigate to="/trust-center" replace />} />
      <Route path="*" element={<Navigate to="/trust-center" replace />} />
    </Routes>
  );
}
